import { createContext, use, type ReactNode } from 'react'

/** A link the site adds to the shell's navigation. */
export interface SiteNavigationLink {
  readonly label: string
  /** A path on this site, or an absolute `https` or `http` URL. */
  readonly target: string
  /** Shown only to signed-in people; otherwise to everyone. */
  readonly signedInOnly: boolean
}

/** What the shell shows of the current site's branding. */
export interface SiteBranding {
  /** The site's logo, in place of the Skillable one; `null` keeps it. */
  readonly logo: { readonly url: string; readonly width: number | null; readonly height: number | null } | null
  /** The site's footer, as HTML the API sanitized; shown sanitized again. */
  readonly footerHtml: string | null
  readonly navigationLinks: readonly SiteNavigationLink[]
}

const SiteBrandingContext = createContext<SiteBranding | null>(null)

/** Gives the shell the current site's branding. Site Management provides it once the site has loaded. */
export function SiteBrandingProvider(props: { readonly branding: SiteBranding | null; readonly children: ReactNode }) {
  return <SiteBrandingContext value={props.branding}>{props.children}</SiteBrandingContext>
}

/** The current site's branding, or `null` until it has loaded or for a site with none. */
export function useSiteBranding(): SiteBranding | null {
  return use(SiteBrandingContext)
}
